import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Typography,
  createStyles,
  withStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import AddonServiceCard, { AddonOption } from "./AddonServiceCard.web";
import FrequencyPill from "./FrequencyPill.web";

export interface AddonPackageSummary {
  name: string;
  description?: string;
  priceLabel: string;
  frequencyLabel: string;
}

interface Props {
  open: boolean;
  packageSummary: AddonPackageSummary;
  addons: AddonOption[];
  selectedIds: number[];
  onToggle: (id: number) => void;
  onCancel: () => void;
  onSkip: () => Promise<void> | void;
  onConfirm: () => Promise<void> | void;
  classes: Record<string, string>;
}

interface State {
  submitting: boolean;
}

const styles = (theme: any) =>
  createStyles({
    paper: {
      width: 500,
      maxWidth: "100vw",
      display: "flex",
      flexDirection: "column",
    },
    title: {
      fontSize: "1.75rem",
      fontWeight: 700,
    },
    packageName: {
      fontSize: "1.375rem",
      fontWeight: 600,
    },
    packageDescription: {
      fontSize: "1rem",
      color: theme.palette.text.secondary,
    },
    packagePriceValue: {
      fontSize: "1.25rem",
      fontWeight: 700,
      whiteSpace: "nowrap",
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "1rem 1.5rem",
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    package: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: "1rem",
      margin: "1rem 1.5rem 0",
      paddingBottom: "1rem",
      borderBottom: `1px solid ${theme.palette.primary.main}`,
    },
    packagePrice: {
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
      flexShrink: 0,
    },
    list: {
      flex: 1,
      overflowY: "auto",
      padding: "1rem 1.5rem",
    },
    footer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "1rem 1.5rem",
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    footerActions: {
      display: "flex",
      gap: "0.5rem",
    },
  });

class AddonServicesPanel extends React.Component<Props, State> {
  state: State = { submitting: false };

  private unmounted = false;

  componentWillUnmount() {
    this.unmounted = true;
  }

  submit = async (action: () => Promise<void> | void) => {
    if (this.state.submitting) {
      return;
    }
    this.setState({ submitting: true });
    try {
      await action();
    } finally {
      if (!this.unmounted) {
        this.setState({ submitting: false });
      }
    }
  };

  render() {
    const {
      open,
      packageSummary,
      addons,
      selectedIds,
      onToggle,
      onCancel,
      onSkip,
      onConfirm,
      classes,
    } = this.props;
    const { submitting } = this.state;

    return (
      <Drawer
        anchor="right"
        open={open}
        onClose={submitting ? undefined : onCancel}
        classes={{ paper: classes.paper }}
        transitionDuration={{ enter: 600, exit: 400 }}
        // MUI skips the slide when the drawer mounts already open; the first open does exactly that.
        SlideProps={{ appear: true }}
      >
        <Box className={classes.header}>
          <Typography className={classes.title}>
            Add services to your package
          </Typography>
          <IconButton aria-label="Close" onClick={onCancel} disabled={submitting}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className={classes.package}>
          <Box>
            <Typography className={classes.packageName}>
              {packageSummary.name}
            </Typography>
            {packageSummary.description && (
              <Typography className={classes.packageDescription}>
                {packageSummary.description}
              </Typography>
            )}
          </Box>
          <Box className={classes.packagePrice}>
            <Typography className={classes.packagePriceValue}>
              {packageSummary.priceLabel}
            </Typography>
            <FrequencyPill label={packageSummary.frequencyLabel} />
          </Box>
        </Box>

        <Box className={classes.list}>
          {addons.map((addon) => (
            <AddonServiceCard
              key={addon.id}
              addon={addon}
              added={selectedIds.includes(addon.id)}
              disabled={submitting}
              onAdd={onToggle}
              onRemove={onToggle}
            />
          ))}
        </Box>

        <Box className={classes.footer}>
          <Button
            variant="outlined"
            startIcon={<CloseIcon />}
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Box className={classes.footerActions}>
            <Button
              variant="outlined"
              endIcon={<ChevronRightIcon />}
              onClick={() => this.submit(onSkip)}
              disabled={submitting}
            >
              Skip
            </Button>
            <Button
              variant="contained"
              color="primary"
              disableElevation
              endIcon={
                submitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <ChevronRightIcon />
                )
              }
              onClick={() => this.submit(onConfirm)}
              disabled={submitting}
            >
              Confirm selection
            </Button>
          </Box>
        </Box>
      </Drawer>
    );
  }
}

export default withStyles(styles)(AddonServicesPanel);
